import { ImmutableObject } from "@hookstate/core"
import { LinearProgress } from "@mui/material"
import { useRouter } from "next/router"
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react"

import { create } from "src/helpers/bem"

import { getUrlParameter } from "src/helpers/urlParams"
import { getProductVariants } from "src/queriesXRM/productVariants"
import { useLocale } from "src/states/locale"
import { addNotification } from "src/states/notifications"
import { useProducts } from "src/states/shop/products"

import {
  mergeProductVariants,
  ProductVariant,
  useProductVariants,
} from "src/states/shop/productVariants"
import { TranslationMessages } from "src/translations"

import { PagesProps } from "src/types/SharedPageProps"

import styles from "./ShopDetail.module.scss"
import { ShopDetailContent } from "./ShopDetailContent"
import { ShopDetailHeader } from "./ShopDetailHeader"

const bem = create(styles, "ShopDetail")

export type ShopDetailProps = {
  pages: PagesProps
  messages: TranslationMessages
}

export const ShopDetail: FC<ShopDetailProps> = ({ pages, messages }) => {
  const locale = useLocale()
  const router = useRouter()
  const m = messages?.pages.shop.detail.snackbar
  const { products, loading: loadingProducts } = useProducts()
  const { variants, loading: loadingProductVariants } = useProductVariants()
  const [productVariant, setProductVariant] = useState(variants[0])
  const [backupProductVariant, setBackupProductVariant] = useState(variants[0])

  const product = products.find((p) => p.id === getUrlParameter("id"))

  const rollbackProductVariant = () => {
    setProductVariant(backupProductVariant) // Rollback to the backup state
  }

  const setRollbackableProductVariant: Dispatch<
    SetStateAction<ImmutableObject<ProductVariant>>
  > = (newVariant) => {
    setProductVariant((current) => {
      setBackupProductVariant(current) // Backup the current state
      if (typeof newVariant === "function") {
        return newVariant(current)
      } else {
        return newVariant
      }
    })
  }

  const loadProductVariants = async () => {
    if (!product) {
      const shopOverviewURL = pages?.data.nodes.find(
        (p) =>
          p.template?.templateName === "Webshop Overview Template" &&
          p.language?.slug === locale,
      )?.uri
      if (shopOverviewURL) {
        addNotification({
          autoclose: true,
          variant: "warning",
          content: `${m?.notFound}`,
        })
        await router.push(shopOverviewURL)
      }
      return
    }
    mergeProductVariants({ loading: true })
    const loadedVariants = await getProductVariants(product)
    mergeProductVariants({
      loading: false,
      variants: loadedVariants,
    })
    if (loadedVariants && loadedVariants.length > 0) {
      setProductVariant(loadedVariants[0] as ImmutableObject<ProductVariant>)
    }
  }

  useEffect(() => {
    if (!loadingProducts) {
      loadProductVariants()
    }
  }, [locale, loadingProducts, messages])

  if (loadingProducts || loadingProductVariants) {
    return <LinearProgress color="primary" />
  }

  if (!product || !productVariant) {
    return <LinearProgress color="primary" />
  }

  return (
    <div className={bem()}>
      <div>
        <ShopDetailHeader
          messages={messages}
          pages={pages}
          product={product}
          rollback={rollbackProductVariant}
          setVariant={setRollbackableProductVariant}
          variant={productVariant}
        />
      </div>
      <div>
        <ShopDetailContent
          messages={messages}
          product={product}
          setVariant={setRollbackableProductVariant}
          variant={productVariant}
        />
      </div>
    </div>
  )
}
